import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProjectAccessConstants } from "../../../../constants";
import { NavigationSearchParams } from "../../../../models";
import {
  SearchProjectOrganisationInvitations,
  SearchProjectOrganisationInvitationsRequest,
  SearchProjectOrganisationInvitationsResponse,
} from "../../../../service/query";
import { useAuth } from "../../../../useAuth";
import { isPollingAbortedError, pollAsync } from "../../../../utils";
import { getProjectAccessDetailsRoute } from "../../../../utils/routes";
import { dataGridMapFilterCriteria, Toast } from "../../../../widget";

const PENDING_PROJECT_ACCESS_REQUEST_POLL_INTERVAL_MS = 15000;

type ProjectAccessRequest = SearchProjectOrganisationInvitationsResponse["results"][number];

interface UsePendingProjectAccessRequestReturnData {
  pendingProjectAccessRequest?: ProjectAccessRequest;
  isPendingProjectAccessRequestLoading: boolean;
  navigateToPendingProjectAccessRequest: () => void;
  shouldRefreshPendingProjectAccessRequest: (shouldPollForRequest?: boolean) => void;
}

/**
 * The current organisation's outstanding request for access to a project it cannot read, sent to the
 * project's responsible owner. Shared by every project template that shows the limited "pre-access" view.
 */
export const usePendingProjectAccessRequest = (
  projectUuid: string | undefined,
  responsibleOwnerOrganisationUuid: string | null | undefined
): UsePendingProjectAccessRequestReturnData => {
  const navigate = useNavigate();
  const { currentUserType, currentOrganisationUuid } = useAuth();

  const [pendingProjectAccessRequest, setPendingProjectAccessRequest] = useState<ProjectAccessRequest>();
  const [isPendingProjectAccessRequestLoading, setIsPendingProjectAccessRequestLoading] = useState(true);
  // Cancels the active polling chain so a replaced or unmounted screen does not keep polling in the background.
  const abortControllerRef = useRef<AbortController | null>(null);
  // Guards state updates from stale async responses when a newer refresh has already started.
  const refreshIdRef = useRef(0);

  const fetchPendingProjectAccessRequest = useCallback(async (): Promise<ProjectAccessRequest | undefined> => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.project = {
      uuid: {
        operator: "eq",
        value: projectUuid,
      },
    };

    filterCriteria.sourceOrganisation = {
      uuid: {
        operator: "eq",
        value: currentOrganisationUuid,
      },
    };

    filterCriteria.targetOrganisation = {
      uuid: {
        operator: "eq",
        value: responsibleOwnerOrganisationUuid,
      },
    };

    filterCriteria.calculatedStatus = {
      operator: "eq",
      value: ProjectAccessConstants.STATUS_SENT,
    };

    const response = await SearchProjectOrganisationInvitations({
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 1,
      },
      filter: {
        results: filterCriteria as unknown as NonNullable<
          NonNullable<SearchProjectOrganisationInvitationsRequest["filter"]>["results"]
        >,
      },
      sort: [{ key: "results.sentDate", direction: "desc" }],
    });

    return response.data?.results?.[0];
  }, [currentOrganisationUuid, responsibleOwnerOrganisationUuid, projectUuid]);

  const shouldRefreshPendingProjectAccessRequest = useCallback(
    (shouldPollForRequest = false): void => {
      const refreshId = refreshIdRef.current + 1;
      refreshIdRef.current = refreshId;
      // Stop any earlier polling chain before starting the next one.
      abortControllerRef.current?.abort();

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      if (!projectUuid || !currentOrganisationUuid || !responsibleOwnerOrganisationUuid) {
        setPendingProjectAccessRequest(undefined);
        setIsPendingProjectAccessRequestLoading(false);
        return;
      }

      setIsPendingProjectAccessRequestLoading(true);

      // Creating the request is eventually consistent, so the just-sent path opts into polling.
      const projectAccessRequest = shouldPollForRequest
        ? pollAsync(fetchPendingProjectAccessRequest, {
            intervalMs: PENDING_PROJECT_ACCESS_REQUEST_POLL_INTERVAL_MS,
            signal: abortController.signal,
          })
        : fetchPendingProjectAccessRequest();

      projectAccessRequest
        .then((request) => {
          // Ignore responses from a superseded refresh or an explicitly aborted polling chain.
          if (refreshIdRef.current !== refreshId || abortController.signal.aborted) {
            return;
          }

          setPendingProjectAccessRequest(request);
          setIsPendingProjectAccessRequestLoading(false);
        })
        .catch((error: unknown) => {
          // Aborted polls are expected when a new refresh starts or the component unmounts.
          if (refreshIdRef.current !== refreshId || isPollingAbortedError(error)) {
            return;
          }

          setPendingProjectAccessRequest(undefined);
          setIsPendingProjectAccessRequestLoading(false);
          Toast.error({ message: "Unable to load project access request" });
        });
    },
    [currentOrganisationUuid, fetchPendingProjectAccessRequest, responsibleOwnerOrganisationUuid, projectUuid]
  );

  const navigateToPendingProjectAccessRequest = useCallback((): void => {
    if (pendingProjectAccessRequest?.uuid && projectUuid) {
      navigate(
        `${getProjectAccessDetailsRoute(
          pendingProjectAccessRequest.uuid,
          currentUserType,
          projectUuid
        )}?${NavigationSearchParams.projectAccessTab}=true`
      );
    }
  }, [currentUserType, navigate, pendingProjectAccessRequest?.uuid, projectUuid]);

  useEffect(() => {
    shouldRefreshPendingProjectAccessRequest();
  }, [shouldRefreshPendingProjectAccessRequest]);

  // Cleanup on unmount to stop any ongoing polling.
  useEffect(() => () => abortControllerRef.current?.abort(), []);

  return {
    pendingProjectAccessRequest,
    isPendingProjectAccessRequestLoading,
    navigateToPendingProjectAccessRequest,
    shouldRefreshPendingProjectAccessRequest,
  };
};
